import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import Body from 'storybookConfig/stories/molecules/Body';

import Heading from 'storybookConfig/stories/molecules/Heading';
import Card from 'storybookConfig/stories/cells/Card';
import deliveryTruckIcon from '../../images/delivery-truck.svg';
import shippingBoxIcon from '../../images/shipping-box.svg';
import lockCheckIcon from '../../images/lock-check.svg';
import openAccessIcon from '../../images/open-access-icon.svg';
import hybridAccessIcon from '../../images/hybrid-access-icon.svg';
import curatedAccessIcon from '../../images/curated-access-icon.svg';
import revSplitIcon from '../../images/rev-split-icon.svg';
import returnPolicyIcon from '../../images/return-policy-icon.svg';
import Divider from 'storybookConfig/stories/molecules/Divider';
import type { MarketplaceFormData } from './types';
import RetailerAccessSlider from './RetailerAccessSlider';
import RevenueSplitPanel from 'components/Common/RevenueSplitPanel';
import FulfillmentTimesPanel from 'components/Common/FulfillmentTimesPanel';

const IconContainer = styled.div`
  width: 160px;
  height: 160px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Title = styled(Heading)`
  text-align: center;
`;

const Subtitle = styled(Body)`
  text-align: center;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  margin-bottom: 10px;
`;

const AccessIcon = styled.img`
  width: 56px;
  height: 56px;
  margin-right: 24px;
  flex-shrink: 0;
  display: block;
`;

const ContentWrapper = styled.div`
  padding: 0 50px;
  text-align: center;
`;

const FooterNote = styled(Body)`
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  text-align: center;
  margin-top: 32px;
  font-size: 14px;
`;

const LearnMoreLink = styled.a`
  color: ${({ theme }) => theme.color.blue500};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ShippingPreview = styled(Flex)`
  gap: 16px;
  margin: 12px 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const RegionCard = styled.div<{ variant: 'us' | 'international' }>`
  flex: 1;
  width: 50%;
  border-radius: 8px;
  overflow: hidden;
`;

const RegionHeader = styled.div<{ variant: 'us' | 'international' }>`
  background: ${({ theme, variant }) =>
    variant === 'us' ? theme.color.blue100 : theme.color.purple100};
  padding: 8px 12px 12px;
`;

const RegionContent = styled.div<{ variant: 'us' | 'international' }>`
  background: ${({ theme, variant }) =>
    variant === 'us' ? theme.color.blue500 : theme.color.purple500};
  padding: 24px;
  margin-top: -8px;
  color: white;
  text-align: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const RegionTitle = styled(Body)`
  color: ${({ theme }) => theme.color.bodyTextPrimary};
`;

const ShippingAmount = styled(Heading)`
  margin-bottom: 16px;
  position: relative;
`;

const FreeShippingPill = styled.div`
  background: white;
  border-radius: 24px;
  padding: 4px 8px;
  display: inline-block;
  font-size: 14px;
  color: ${({ theme }) => theme.color.blue500};
  position: absolute;
  white-space: nowrap;
  bottom: -24px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  left: 50%;
  transform: translateX(-50%);
`;

const CardSection = styled(Flex)`
  gap: 12px;
  margin-top: 12px;
  justify-content: center;
`;

const InfoSection = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoCard = styled(Card)`
  flex: 1;
  padding: 24px;
  border-radius: 12px;
  gap: 12px;
  background: ${({ theme }) => theme.color.white};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  text-align: left;
  max-width: 450px;
`;

const InfoLabel = styled.div<{ variant: 'blue' | 'green' }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  font-weight: 500;
  background: ${({ theme, variant }) => (variant === 'blue' ? '#475569' : theme.color.green500)};
  margin-bottom: 12px;
  width: max-content;
`;

const AccessCard = styled.div`
  background: #f7f9fb;
  border-radius: 12px;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  width: 100%;
`;

const AccessText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

interface AdditionalInfoDescriptionProps {
  marketplaceData: MarketplaceFormData | undefined;
  focusedField: string | null;
  setMarketplaceData: (values: Partial<MarketplaceFormData>) => void;
}

// Helper function to format currency values with 2 decimal places
const formatCurrency = (value: string | number | undefined): string => {
  if (!value) return '0.00';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(numValue) ? '0.00' : numValue.toFixed(2);
};
const AdditionalInfoDescription = ({
  marketplaceData,
  focusedField,
  setMarketplaceData,
}: AdditionalInfoDescriptionProps) => {
  const isInternationalShippingDisabled = !marketplaceData?.shippingRates?.international;

  const renderRevenueSplitPanel = () => (
    <RevenueSplitPanel
      revSplit={marketplaceData?.revSplit}
      iconSrc={revSplitIcon}
      iconAlt="Revenue Split"
      onRevSplitChange={(value) => setMarketplaceData({ revSplit: value })}
    />
  );

  const renderFulfillmentTimesPanel = () => (
    <FulfillmentTimesPanel iconSrc={deliveryTruckIcon} iconAlt="Fulfillment Times" />
  );

  const renderFlatRateShippingPanel = () => (
    <Card>
      <ContentWrapper>
        <IconContainer>
          <img src={shippingBoxIcon} alt="Flat Rate Shipping" />
        </IconContainer>
        <Title variant="Headings/H3">Flat Rate Shipping</Title>
        <Subtitle>
          Establish your shipping rates according to the locations you are shipping to and from. You
          can specify the countries to which you offer international shipping.
        </Subtitle>
      </ContentWrapper>
      <ContentWrapper>
        <ShippingPreview>
          <RegionCard variant="us">
            <RegionHeader variant="us">
              <RegionTitle variant="Body/Regular">Domestic</RegionTitle>
            </RegionHeader>
            <RegionContent variant="us">
              <ShippingAmount variant="Headings/H1">
                ${formatCurrency(marketplaceData?.shippingRates?.us?.flatRate)}
                {Number(marketplaceData?.shippingRates?.us?.freeShippingThreshold) > 0 && (
                  <FreeShippingPill>
                    Free over $
                    {formatCurrency(marketplaceData?.shippingRates?.us?.freeShippingThreshold)}
                  </FreeShippingPill>
                )}
              </ShippingAmount>
            </RegionContent>
          </RegionCard>
          {!isInternationalShippingDisabled && marketplaceData.shippingRates?.international && (
            <RegionCard variant="international">
              <RegionHeader variant="international">
                <RegionTitle variant="Body/Regular">International</RegionTitle>
              </RegionHeader>
              <RegionContent variant="international">
                <ShippingAmount variant="Headings/H1">
                  ${formatCurrency(marketplaceData?.shippingRates?.international?.flatRate)}
                  {Number(marketplaceData?.shippingRates?.international?.freeShippingThreshold) >
                    0 && (
                    <FreeShippingPill>
                      Free over $
                      {formatCurrency(
                        marketplaceData.shippingRates?.international?.freeShippingThreshold
                      )}
                    </FreeShippingPill>
                  )}
                </ShippingAmount>
              </RegionContent>
            </RegionCard>
          )}
        </ShippingPreview>
        <Divider margin="24px 0" />
        <CardSection>
          <InfoCard>
            <InfoLabel variant="blue">Flat Rate</InfoLabel>
            <InfoSection>
              <Body variant="Body/Body Small" color="bodyTextPrimary">
                Amount:
              </Body>
              <Body
                variant="Body/Body Small"
                color="bodyTextSecondary"
                style={{ marginLeft: '8px' }}
              >
                The flat rate shipping amount you wish to charge for each transaction conducted by
                your partners.
              </Body>
            </InfoSection>
            <InfoSection>
              <Body variant="Body/Body Small" color="bodyTextPrimary">
                Shipping Description:
              </Body>
              <Body
                variant="Body/Body Small"
                color="bodyTextSecondary"
                style={{ marginLeft: '8px' }}
              >
                Description you want to appear on invoices or receipts when flat rate shipping is
                applied to an order.
              </Body>
            </InfoSection>
          </InfoCard>

          <InfoCard>
            <InfoLabel variant="green">Free Shipping</InfoLabel>
            <InfoSection>
              <Body variant="Body/Body Small" color="bodyTextPrimary">
                Amount:
              </Body>
              <Body
                variant="Body/Body Small"
                color="bodyTextSecondary"
                style={{ marginLeft: '8px' }}
              >
                The minimum purchase amount of your products that qualifies customers for free
                shipping, replacing the standard flat rate.
              </Body>
            </InfoSection>
            <InfoSection>
              <Body variant="Body/Body Small" color="bodyTextPrimary">
                Shipping Description:
              </Body>
              <Body
                variant="Body/Body Small"
                color="bodyTextSecondary"
                style={{ marginLeft: '8px' }}
              >
                Description you want to appear on invoices or receipts when free shipping is applied
                to an order.
              </Body>
            </InfoSection>
          </InfoCard>
        </CardSection>
      </ContentWrapper>
    </Card>
  );

  const renderRetailerAccessPanel = () => (
    <Card>
      <ContentWrapper>
        <IconContainer>
          <img src={lockCheckIcon} alt="Retailer Access" />
        </IconContainer>
        <Title variant="Headings/H3">How do you want to approve partnerships?</Title>
        <Subtitle>
          Use the slider or input field to set your minimum retailer revenue preference. Choose
          &quot;Open Access&quot; for automatic partnerships without approval, or &quot;Curated
          Access&quot; to manually approve all partnerships. Additionally, there&apos;s a hybrid
          option that allows you to set your preference based on annual revenue.
        </Subtitle>
      </ContentWrapper>
      <Divider />
      <ContentWrapper>
        <RetailerAccessSlider
          value={marketplaceData?.retailerAccess || 'OPEN'}
          approvalThreshold={marketplaceData?.approvalThreshold}
          onSliderChange={(value: string, threshold?: number) => {
            setMarketplaceData({
              retailerAccess: value,
              approvalThreshold: value === 'HYBRID' ? threshold : undefined,
            });
          }}
        />
      </ContentWrapper>
      <Divider />
      <Flex gap="16px">
        {marketplaceData?.retailerAccess === 'OPEN' && (
          <AccessCard>
            <AccessIcon src={openAccessIcon} alt="" />
            <AccessText>
              <Body variant="Headings/H3" color="bodyTextPrimary" style={{ marginBottom: '8px' }}>
                Open Access
              </Body>
              <Body variant="Body/Regular" color="bodyTextSecondary" style={{ lineHeight: '1.5' }}>
                While Modern Dropship enables retailers to quickly add your products to their stores
                without requiring manual approval, it does mean{' '}
                <strong style={{ color: '#222f3e', fontWeight: 600 }}>
                  relinquishing some control
                </strong>{' '}
                over who can sell your items.
              </Body>
            </AccessText>
          </AccessCard>
        )}

        {marketplaceData?.retailerAccess === 'HYBRID' && (
          <AccessCard>
            <AccessIcon src={hybridAccessIcon} alt="" />
            <AccessText>
              <Body variant="Headings/H3" color="bodyTextPrimary" style={{ marginBottom: '8px' }}>
                Hybrid Access
              </Body>
              <Body variant="Body/Regular" color="bodyTextSecondary" style={{ lineHeight: '1.5' }}>
                If you&apos;re looking to collaborate with retaillers, it&apos;s essential to target
                those that acheive at least{' '}
                <strong style={{ color: '#222f3e', fontWeight: 600 }}>
                  ${(marketplaceData?.approvalThreshold || 0).toLocaleString()}
                </strong>{' '}
                in annual revenue. This threshold ensures that you&apos;re partnering with
                retaillers that have a solid financial foundation.
              </Body>
            </AccessText>
          </AccessCard>
        )}

        {marketplaceData?.retailerAccess === 'CURATED' && (
          <AccessCard>
            <AccessIcon src={curatedAccessIcon} alt="" />
            <AccessText>
              <Body variant="Headings/H3" color="bodyTextPrimary" style={{ marginBottom: '8px' }}>
                Curated Access
              </Body>
              <Body variant="Body/Regular" color="bodyTextSecondary" style={{ lineHeight: '1.5' }}>
                This option allows you to maintain greater control over your brand while
                collaborating with select retailers. However, it does rquire you to{' '}
                <strong style={{ color: '#222f3e', fontWeight: 600 }}>manage more tasks</strong>, as
                retailers will need to send connection requests that{' '}
                <strong style={{ color: '#222f3e', fontWeight: 600 }}>
                  you need to review and approve
                </strong>{' '}
                before they add your products.
              </Body>
            </AccessText>
          </AccessCard>
        )}
      </Flex>
    </Card>
  );

  const renderReturnPolicyPanel = () => (
    <Card>
      <ContentWrapper>
        <IconContainer>
          <img src={returnPolicyIcon} alt="Return Policy" />
        </IconContainer>
        <Title variant="Headings/H3">Return Policy</Title>
        <Subtitle>Link to your return policy for potential retail partners to reference.</Subtitle>
      </ContentWrapper>
      <Divider />
      <ContentWrapper>
        <FooterNote>
          <LearnMoreLink href="#">Learn more</LearnMoreLink> about the implications of this choice.
        </FooterNote>
      </ContentWrapper>
    </Card>
  );

  return (
    <Flex column gap="32px">
      {(!focusedField || focusedField === 'fulfillmentTimes') && renderFulfillmentTimesPanel()}
      {focusedField === 'returnPolicy' && renderReturnPolicyPanel()}
      {focusedField === 'revSplit' && renderRevenueSplitPanel()}
      {focusedField === 'retailerAccess' && renderRetailerAccessPanel()}
      {focusedField === 'shippingRates' && renderFlatRateShippingPanel()}
    </Flex>
  );
};

export default AdditionalInfoDescription;
